// Turns the "Recommended For You" preview grid (the .add-swiper product grid in
// snippets/predictive-search-preview.liquid) into a Swiper carousel showing 3.5
// slides, only between 1024px and 1439px viewports. Outside that range the <li>s
// are moved back to being direct children of the <ul> so the plain CSS grid
// (.predictive-search__product-grid) applies as normal.
const PREDICTIVE_SEARCH_SWIPER_QUERY = '(min-width: 1024px) and (max-width: 1439px)';
const PREDICTIVE_SEARCH_SWIPER_SELECTOR = '.predictive-search__product-grid.add-swiper';

function enablePredictiveSearchSwiper(grid) {
  if (!grid || grid.predictiveSearchSwiper) return;
  // Only the 1024-1439px range ever builds this carousel, so the Swiper bundle
  // is fetched on demand here instead of being parsed on every page. Replaces a
  // 20x50ms poll that only existed because the bundle was a deferred <script>.
  if (typeof Swiper === 'undefined') {
    window.loadSwiper().then(() => enablePredictiveSearchSwiper(grid));
    return;
  }

  const slides = Array.from(grid.children);
  if (slides.length < 2) return;

  const wrapper = document.createElement('div');
  wrapper.className = 'swiper-wrapper';
  slides.forEach((slide) => {
    slide.classList.add('swiper-slide');
    wrapper.appendChild(slide);
  });
  grid.appendChild(wrapper);
  grid.classList.add('swiper');
  grid.classList.add('predictive-search__product-grid--swiper');

  const modal = grid.closest('details-modal');
  grid.predictiveSearchSwiper = new Swiper(grid, {
    slidesPerView: 3.435,
    spaceBetween: 1,
    on: {
      touchStart: () => { if (modal) modal._swiperDragging = true; },
      touchEnd:   () => { if (modal) setTimeout(() => { modal._swiperDragging = false; }, 100); },
    },
  });
}

function disablePredictiveSearchSwiper(grid) {
  if (!grid || !grid.predictiveSearchSwiper) return;

  grid.predictiveSearchSwiper.destroy(true, true);
  grid.predictiveSearchSwiper = null;
  grid.classList.remove('swiper');
  grid.classList.remove('predictive-search__product-grid--swiper');

  const wrapper = grid.querySelector(':scope > .swiper-wrapper');
  if (wrapper) {
    Array.from(wrapper.children).forEach((slide) => {
      slide.classList.remove('swiper-slide');
      slide.removeAttribute('style');
      grid.appendChild(slide);
    });
    wrapper.remove();
  }
}

function syncPredictiveSearchSwipers() {
  const withinBreakpoint = window.matchMedia(PREDICTIVE_SEARCH_SWIPER_QUERY).matches;

  document.querySelectorAll(PREDICTIVE_SEARCH_SWIPER_SELECTOR).forEach((grid) => {
    if (withinBreakpoint) {
      enablePredictiveSearchSwiper(grid);
    } else {
      disablePredictiveSearchSwiper(grid);
    }
  });
}

const predictiveSearchSwiperMediaQuery = window.matchMedia(PREDICTIVE_SEARCH_SWIPER_QUERY);
predictiveSearchSwiperMediaQuery.addEventListener('change', syncPredictiveSearchSwipers);

window.addEventListener('load', syncPredictiveSearchSwipers);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncPredictiveSearchSwipers);
} else {
  syncPredictiveSearchSwipers();
}

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.blinker = this.querySelector('.blinker');
    this.mirror = this.querySelector('.search__input-mirror');

    this.setupEventListeners();
    this.setupBlinker();
  }

  setupEventListeners() {
    if (!this.input) return;

    this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('blur', () => {
      const shouldIgnoreBlur = window.matchMedia('(pointer: coarse)').matches && document.activeElement === document.body;

      if (!shouldIgnoreBlur) return;

      const modal = this.closest('details-modal');
      if (modal) {
        modal._keyboardDismissed = true;
        window.setTimeout(() => {
          modal._keyboardDismissed = false;
        }, 250);
      }
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    let touchStartY = 0;
    document.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
      if (this.contains(e.target)) this._touchingComponent = true;
    }, { passive: true });
    document.addEventListener('touchend', (e) => {
      const deltaY = e.changedTouches[0].clientY - touchStartY;
      const isUpSwipe = deltaY < -30;
      const inputFocused = document.activeElement === this.input;

      if (isUpSwipe && inputFocused) {
        this._dismissingKeyboard = true;
        this.input.blur();
        setTimeout(() => { this._dismissingKeyboard = false; }, 200);
      }

      setTimeout(() => { this._touchingComponent = false; }, 50);
    }, { passive: true });

    // iOS Safari blurs the focused input as soon as the user scrolls the results
    // (its way of dismissing the keyboard), which fires focusout even though the
    // user never left the search UI. Track an active touch-scroll inside the
    // results so onFocusOut can ignore that blur instead of closing the dropdown.
    this.predictiveSearchResults.addEventListener('touchstart', () => {
      this.isTouchScrolling = true;
    }, { passive: true });
    this.predictiveSearchResults.addEventListener('touchend', () => {
      this.isTouchScrolling = false;
    }, { passive: true });
  }

  // Unlike the search results page, this dropdown closes on blur, so the fake
  // cursor has to track the typed text while the input stays focused, taking
  // over from (and hiding) the real caret rather than only appearing on blur.
  setupBlinker() {
    if (!this.blinker || !this.mirror || !this.input) return;

    const inputStyle = window.getComputedStyle(this.input);
    ['fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'letterSpacing', 'textTransform'].forEach((prop) => {
      this.mirror.style[prop] = inputStyle[prop];
    });

    this.updateBlinkerPosition = this.updateBlinkerPosition.bind(this);
    this.input.addEventListener('input', this.updateBlinkerPosition);
    this.input.addEventListener('focus', this.updateBlinkerPosition);
    this.input.addEventListener('blur', this.updateBlinkerPosition);
    this.updateBlinkerPosition();
  }

  updateBlinkerPosition() {
    const hasText = this.input.value.length > 0;

    if (hasText) {
      this.mirror.textContent = this.input.value;
      const paddingLeft = parseFloat(window.getComputedStyle(this.input).paddingLeft) || 0;
      const textWidth = this.mirror.getBoundingClientRect().width;
      this.input.style.caretColor = 'transparent';
      this.blinker.style.marginLeft = '1px';
      this.blinker.style.left = `${paddingLeft + textWidth + 1}px`;
      this.blinker.style.width = '2px';
      this.blinker.style.setProperty('display', 'block', 'important');
    } else {
      this.input.style.caretColor = '';
      this.blinker.style.marginLeft = '';
      this.blinker.style.left = '';
      this.blinker.style.width = '';
      this.blinker.style.removeProperty('display');
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Clear the previous term's results before the new fetch resolves. This
      // removes the whole #predictive-search-results block - not just the inner
      // groups-wrapper - so the "See all results for ..." button and loading
      // spinner (its siblings, not its children) go with it instead of being
      // left behind next to nothing, which is what read as a broken,
      // half-rendered dropdown. Clearing eagerly also means the previous
      // (differently sized) result set doesn't sit on screen for the whole
      // fetch round-trip only to visibly snap to the new size ~1-2s later once
      // the real results for this term arrive.
      this.querySelector('#predictive-search-results')?.remove();
      this.predictiveSearchResults.querySelector('.search-preview')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
    this.updateBlinkerPosition?.();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    const sideMenu = document.querySelector(".menu-drawer-container");
    if (sideMenu && sideMenu.hasAttribute("open")) {
      const detailsAnimated = sideMenu.closest("details-animated");
      if (detailsAnimated) {
        detailsAnimated.shrink();
      } else {
        sideMenu.removeAttribute("open");
        sideMenu.setAttribute("data-animating", "false");
      }
    }
    const detailsModal = document.querySelector('details-modal');
    const summaryElement = detailsModal.querySelector('summary');
    if (detailsModal) {
      detailsModal.open(summaryElement);
    }


    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    const modal = this.closest('details-modal');
    const shouldIgnoreBlur = this._dismissingKeyboard ||
      this._touchingComponent ||
      (modal && modal._keyboardDismissed) ||
      (window.matchMedia('(pointer: coarse)').matches && !event?.relatedTarget && document.activeElement === document.body);

    if (shouldIgnoreBlur) return;

    setTimeout(() => {
      if (this.isTouchScrolling) return;
      if (!this.contains(document.activeElement) && !this._dismissingKeyboard && !this._touchingComponent) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter': {
        const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
        if (selectedOption) {
          event.preventDefault();
          this.selectOption();
        } else {
          event.preventDefault();
          this.submitSearchForm();
        }
        break;
      }
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    if (event.code === 'Enter' || event.key === 'Enter' || event.keyCode === 13) {
      const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (!selectedOption) {
        event.preventDefault();
        this.submitSearchForm();
      }
    }
  }

  submitSearchForm() {
    const form = this.input?.form;
    if (!form) return;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
      //   // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
      //   return;
      // }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    // Cancel any in-flight request from a previous keystroke so its response
    // can't arrive late and overwrite results for the current search term.
    this.abortController.abort();
    this.abortController = new AbortController();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.renderSearchResultsCount(searchTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=query,product`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        // Guard against a stale response resolving after the term has since changed.
        if (searchTerm !== this.searchTerm) return;

        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        this.renderSearchResultsCount(searchTerm);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  renderSearchResultsCount(term) {
    const resultsCount = this.querySelectorAll('[data-predictive-search-query]');
    
    resultsCount.forEach(result => {
      const resultValue = result.textContent.trim();
      const str = resultValue.split(" ");
      const value = resultValue.split("(");
      let searchTerm = value[0].replaceAll(" ", "+").toLowerCase();

      // Bold the matched term immediately, don't wait on the product count fetch below.
      result.innerHTML = this.highlightText(resultValue, term);

      fetch(`/search?q=${searchTerm}&type=product&_pos=${str.length}_psq=${str[1]}&_ss=e&_v=1.0`, {
        signal: this.abortController?.signal,
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(htmlString => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlString, 'text/html');
          const dataProducts = doc.querySelectorAll('#product-grid-container li');
          const productCount = dataProducts.length;
          result.dataset.productCount = productCount;
          const listItem = result.closest('.predictive-search__list-item');
          listItem.insertAdjacentHTML('beforeend', `<span class="predictive-search__item-count body-text">${productCount}</span>`);
        })
        .catch(error => {
          console.error(`Error fetching product count for "${searchTerm}":`, error);
        });
    });
  }
  highlightText(textValue, term) {
    // Capitalize here (rather than via CSS text-transform) because the <strong> wrap
    // below splits the text into separate fragments, and browsers apply text-transform
    // per-fragment — capitalizing the first letter of the text *after* the <strong> too
    // (e.g. "hoodie" -> "HOodie" instead of "Hoodie").
    const capitalized = textValue.replace(/\b\w/g, (char) => char.toUpperCase());
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    return capitalized.replace(regex, '<strong>$1</strong>');
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults
      .querySelectorAll(PREDICTIVE_SEARCH_SWIPER_SELECTOR)
      .forEach(disablePredictiveSearchSwiper);
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.predictiveSearchResults.classList.toggle(
      'predictive-search--no-results',
      !!this.predictiveSearchResults.querySelector(':scope > .search-preview')
    );
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.renderWishlistButtons();
    syncPredictiveSearchSwipers();
  }

  // The "Collections" wishlist heart button on other product cards (collections,
  // related products) is auto-injected by the Swym app-embed script, which only
  // scans the page once on load. Cards rendered here via JS never get scanned, so
  // we build the same button markup ourselves (see renderCardWishlistButtons in
  // swym-custom.js) and reuse the theme's existing wishlist click handling (see
  // [data-wishlist-trigger] in swym-custom.js).
  renderWishlistButtons() {
    window.swymcsWishlistFunctions?.renderCardWishlistButtons(this.predictiveSearchResults);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    this.resultsMaxHeight = window.innerHeight - headerBottom;
    return this.resultsMaxHeight;
  }

  updateResultsMaxHeight() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  // A single synchronous read at the moment we open isn't enough on its own:
  // Android resizes window.innerHeight for the keyboard (so recomputing once
  // at open() covers it), but iOS keeps innerHeight fixed and instead settles
  // the header position asynchronously (scroll-into-view + keyboard slide-up),
  // so that same read can land mid-transition there. Re-checking over the
  // next few frames, then once more after the animation window, self-corrects
  // once things actually settle on whichever platform - instead of trusting
  // one snapshot in time.
  scheduleResultsMaxHeightSettle() {
    // renderSearchResults() calls open() (and so this) again on every keystroke.
    // Without cancelling the previous cycle here, each keystroke leaves its own
    // ~500ms rAF+timeout chain running in the background — several of them
    // overlap while typing, and whichever stale one finishes last overwrites
    // max-height well after its own results are gone, reading as the dropdown
    // changing height on its own a moment after the user stopped typing.
    if (this._settleRaf) cancelAnimationFrame(this._settleRaf);
    if (this._settleTimeout) window.clearTimeout(this._settleTimeout);

    let attempts = 0;
    const recheck = () => {
      if (!this.isOpen) return;
      this.updateResultsMaxHeight();
      attempts += 1;
      if (attempts < 10) {
        this._settleRaf = requestAnimationFrame(recheck);
      } else {
        this._settleTimeout = window.setTimeout(() => {
          if (this.isOpen) this.updateResultsMaxHeight();
        }, 350);
      }
    };
    recheck();
  }

  open() {
    this.updateResultsMaxHeight();
    const hasProductResults = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    this.predictiveSearchResults.style.padding = hasProductResults ? '2.4rem 1.6rem 2.4rem 3.8rem' : '';
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    this.scheduleResultsMaxHeightSettle();

    // Keep it correct for as long as the panel stays open too - scrolling
    // while it's open can still toggle a mobile browser's address bar.
    if (!this._viewportResizeHandler) {
      this._viewportResizeHandler = () => this.updateResultsMaxHeight();
      window.addEventListener('resize', this._viewportResizeHandler);
      window.visualViewport?.addEventListener('resize', this._viewportResizeHandler);
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;

    if (this._settleRaf) cancelAnimationFrame(this._settleRaf);
    if (this._settleTimeout) window.clearTimeout(this._settleTimeout);
    this._settleRaf = null;
    this._settleTimeout = null;

    if (this._viewportResizeHandler) {
      window.removeEventListener('resize', this._viewportResizeHandler);
      window.visualViewport?.removeEventListener('resize', this._viewportResizeHandler);
      this._viewportResizeHandler = null;
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.predictiveSearchResults.classList.remove('predictive-search--no-results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
